import { CheckOutlined, CloseCircleFilled, CloseOutlined } from '@ant-design/icons';
import { Divider, Popover, Space, Tooltip } from 'antd';
import dayjs from 'dayjs';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import {
  ContextButton,
  ExportButton,
  IconContextButton,
  ScopeButton,
} from '@monorepo/shared-lib';
import { ReactComponent as EllipseIcon } from 'assets/images/icons/ellipsesIcon.svg';
import { ReactComponent as RefreshIcon } from 'assets/images/icons/refreshIcon.svg';
import { StyledPopconfirm } from 'components/Custom/Popconfirm';
import { QuestionMarkTooltip } from 'components/Custom/QuestionMarkTooltip';
import { CardTitleSpan, ColoredTag } from 'components/Custom/spans';
import SwitchLabeled from 'components/Custom/SwitchLabeled';
import { MessageStates } from 'containers/MessageBox';
import GoogleDriveIcon from 'shared/assets/images/google_drive.svg';
import OneDriveIcon from 'shared/assets/images/onedrive.svg';
import { turnMessageOn } from 'store/app/actions';
import type { MessageData } from 'store/app/types';
import {
  disconnectFolderRequest,
  generateScanEmail,
  getDriveScannerStatus,
  getScanEmailStatus,
} from 'store/organisation/actions';
import {
  getGoogleDriveStatus,
  getOneDriveStatus,
  getScanEmailDetails,
} from 'store/organisation/selectors';
import { CardInfo } from './card-info';
import DriveUploadHistory from './drive-upload-history';
import GoogleDrivePicker from './GoogleDrive/google-drive-picker';
import OneDrivePicker from './OneDrive/one-drive-picker';
import { ConfirmModal } from 'components/Custom/ConfirmModal';

/** Label → value spacing inside connected-folder metadata (both providers). */
const CONNECTED_FOLDER_LABEL_DATA_GAP = 6;

function formatDriveScannerCreatedDate(raw: unknown): string | null {
  if (raw == null || raw === '') return null;
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n)) return null;
  const d = dayjs(n);
  return d.isValid() ? d.format('DD/MM/YYYY HH:mm') : null;
}

/** Folder resource id from status (camelCase/snake_case or legacy API keys). */
function pickDriveFolderId(status: Record<string, unknown> | undefined | null) {
  if (status == null || typeof status !== 'object') return null;
  for (const key of [
    'folderId',
    'folder_id',
    'resourceId',
    'googleFolderId',
    'fileId',
  ]) {
    const v = status[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  }
  return null;
}

/** Browser URL from scanner status (`driveUrl` from register/status API). */
function pickDriveUrl(status: Record<string, unknown> | undefined | null) {
  if (status == null || typeof status !== 'object') return null;
  for (const key of ['driveUrl', 'drive_url']) {
    const v = status[key];
    if (typeof v === 'string' && /^https?:\/\//i.test(v.trim())) {
      return v.trim();
    }
  }
  return null;
}

/** Human-readable folder path for display (Graph `drivePath` is not user-facing). */
function formatDrivePathDisplay(
  drivePath: string | null | undefined,
  folderName: string | null | undefined,
  provider: 'google' | 'oneDrive'
): string | null {
  const path = typeof drivePath === 'string' ? drivePath.trim() : '';
  if (/^https?:\/\//i.test(path)) return path;

  const rootMatch = path.match(/root:(?:\/(.*))?$/i);
  if (rootMatch?.[1]) {
    const segment = decodeURIComponent(rootMatch[1].trim());
    if (segment) {
      return provider === 'google' ? `My Drive/${segment}` : segment;
    }
  }

  if (folderName) {
    return provider === 'google' ? `My Drive/${folderName}` : folderName;
  }

  return path || null;
}

/** Open folder in browser: prefer API `driveUrl`; else derive from path/IDs. */
function resolveDriveBrowseUrl(params: {
  provider: 'google' | 'oneDrive';
  driveUrl?: string | null;
  drivePath?: string | null;
  folderId?: string | null;
  driveId?: string | null;
}): string | null {
  const apiUrl =
    typeof params.driveUrl === 'string' ? params.driveUrl.trim() : '';
  if (/^https?:\/\//i.test(apiUrl)) {
    try {
      return new URL(apiUrl).href;
    } catch {
      return apiUrl;
    }
  }

  const trimmedPath =
    typeof params.drivePath === 'string' ? params.drivePath.trim() : '';
  if (/^https?:\/\//i.test(trimmedPath)) {
    try {
      return new URL(trimmedPath).href;
    } catch {
      return trimmedPath;
    }
  }

  const folderId =
    typeof params.folderId === 'string' ? params.folderId.trim() : '';
  if (!folderId) return null;

  if (params.provider === 'google') {
    return `https://drive.google.com/drive/folders/${encodeURIComponent(
      folderId
    )}`;
  }

  const id = encodeURIComponent(folderId);
  const rawDrive =
    params.driveId != null &&
    typeof params.driveId === 'string' &&
    params.driveId.trim() !== ''
      ? params.driveId.trim()
      : null;
  const query = rawDrive
    ? `cid=${encodeURIComponent(rawDrive)}&id=${id}`
    : `id=${id}`;
  return `https://onedrive.live.com/?${query}`;
}

// ---------------------------------------------------------------------------
// Styled — inline chip (scan header)
// ---------------------------------------------------------------------------

const Wrapper = styled.div<{ $sidePanel?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: ${(p) => (p.$sidePanel ? '100%' : '35%')};
  max-width: ${(p) => (p.$sidePanel ? '100%' : '35%')};
  margin-left: ${(p) => (p.$sidePanel ? '0' : 'auto')};
  align-self: flex-start;
  padding: 12px 16px;
  border-radius: 16px;
  border: 1px solid var(--color-border, #dbeae3);
  background-color: var(--color-surface, #f3f7f8);
  position: relative;
  box-sizing: border-box;
`;

/** Side column: stacked capture panels (folder + email). */
const SidePanelStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

/** Matches VAT Tax Returns entity details card: white surface, light border, soft shadow */
const SidePanelOuter = styled.div<{ $interactionDisabled?: boolean }>`
  position: relative;
  width: 100%;
  box-sizing: border-box;
  background-color: var(--color-surface-raised, #ffffff);
  border: 1px solid var(--color-border, #dbeae3);
  border-radius: 10px;
  box-shadow: 0px 0px 10px 0px #0000000d;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 0;
  ${(p) =>
    p.$interactionDisabled
      ? `
    opacity: 0.55;
    pointer-events: none;
    user-select: none;

    @media (max-width: 1500px) {
      opacity: 1;
    }
  `
      : ''}
`;

/** Title, divider (same as EntityDetailsCard), then intro text below the line */
const SidePanelHead = styled.div`
  width: 100%;
`;

const SidePanelBodyColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const HistoryCorner = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 1;
`;

/** Matches EntityDetailsCard header row spacing above the divider */
const SidePanelTitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;
  padding-right: 40px;
  box-sizing: border-box;
  margin-bottom: 16px;
`;

const SidePanelTitleRowWithActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
  min-width: 0;
`;

const SidePanelIntroText = styled.p`
  margin: 0 0 16px;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.45;
  color: var(--color-text-muted, #5a6a6a);
  padding-right: 40px;
  box-sizing: border-box;
`;

const VerticalPickersColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  width: 100%;

  .picker-row-item {
    width: 100%;
    display: flex;
    justify-content: flex-start;
  }
`;

const ProviderTilesRow = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-top: 30px;
`;

const ProviderTile = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  min-width: 0;
  text-align: center;
`;

const PickerIconButton = styled.button<{ $disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  padding: 10px;
  border-radius: 8px;
  border: 1px solid var(--color-border, #dbeae3);
  background-color: var(--color-surface-raised, #ffffff);
  box-sizing: border-box;
  cursor: ${(p) => (p.$disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(p) => (p.$disabled ? 0.5 : 1)};
`;

const PickerIconImg = styled.img`
  width: 24px;
  height: 24px;
  object-fit: contain;
`;

const ProviderTileLabel = styled.div`
  font-size: 13px;
  font-weight: 400;
  color: var(--color-text-dark, #042a2a);
  line-height: 1.3;
  word-break: break-word;
`;

const SyncBlock = styled.div`
  width: 100%;
  margin-top: 20px;
`;

const ConnectedStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const ConnectedBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const ConnectedTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
`;

const ConnectedProviderStart = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const ConnectedMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  min-width: 0;
`;

const MetaRow = styled.div`
  width: 100%;
  min-width: 0;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
  width: 100%;
`;

const LabelRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-dark, #042a2a);
`;

const PickerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 30px;
  flex-wrap: wrap;
  justify-content: center;
  width: 100%;
`;

const ConnectedList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
  color: var(--color-text-muted, #5a6a6a);
  font-style: italic;
  line-height: 1.2;
`;

const ConnectedItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  justify-content: center;
  width: 100%;
`;

const ProviderIcon = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;
  flex-shrink: 0;
`;

const ProviderNameText = styled.span<{ $weight?: number }>`
  font-family: Inter, sans-serif;
  font-size: 13px;
  font-weight: ${(p) => (p.$weight != null ? p.$weight : 400)};
  color: var(--color-text-dark, #042a2a);
`;

const InlinePickerGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DisconnectGlyph = styled(CloseCircleFilled)`
  font-size: 14px;
`;

const FolderName = styled.span`
  display: block;
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
  padding: 0 4px;
`;

const IconActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin: 0;
  padding: 1px;
  border: none;
  background: none;
  cursor: pointer;
  color: var(--color-text-muted, #5a6a6a);
  line-height: 1;
  flex-shrink: 0;
  border-radius: 4px;
  box-sizing: border-box;

  &:hover {
    background-color: var(--color-layout-bg, #e9f6f7);
    color: var(--color-text-dark, #042a2a);
  }
`;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ConnectFolderProps {
  connectionId: string;
  /** Wide chip beside scan title vs narrow fixed column on Capture tab */
  layout?: 'default' | 'sidePanel';
  /** When true, show side panel UI but disable all connect actions */
  disabled?: boolean;
  /** Show the disabled info message (used for Excel entities only). */
  showDisabledMessage?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ConnectFolder: React.FC<ConnectFolderProps> = ({
  connectionId,
  layout = 'default',
  disabled = false,
  showDisabledMessage = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const googleDriveStatus = useSelector(getGoogleDriveStatus) as any;
  const oneDriveStatus = useSelector(getOneDriveStatus) as any;
  const scanEmailDetails = useSelector(getScanEmailDetails) as any;

  const [googleDisconnected, setGoogleDisconnected] = React.useState(false);
  const [oneDriveDisconnected, setOneDriveDisconnected] = React.useState(false);
  const [syncExistingFiles, setSyncExistingFiles] = React.useState(false);
  const [emailCaptureMenuOpen, setEmailCaptureMenuOpen] = React.useState(false);
  const [folderCaptureMenuOpen, setFolderCaptureMenuOpen] = React.useState(false);
  const [regenerateEmailModalOpen, setRegenerateEmailModalOpen] =
    React.useState(false);
  const [disconnectFolderModalTarget, setDisconnectFolderModalTarget] =
    React.useState<'one' | 'google' | null>(null);

  React.useEffect(() => {
    if (connectionId) {
      dispatch(getDriveScannerStatus(connectionId));
      dispatch(getScanEmailStatus(connectionId));
    }
  }, [connectionId, dispatch]);

  React.useEffect(() => {
    if (googleDriveStatus?.folderName) setGoogleDisconnected(false);
  }, [googleDriveStatus?.folderName]);

  React.useEffect(() => {
    if (oneDriveStatus?.folderName) setOneDriveDisconnected(false);
  }, [oneDriveStatus?.folderName]);

  const generateEmail = () => {
    dispatch(generateScanEmail(connectionId));
  };

  const handleCopyCaptureEmail = React.useCallback(
    (email: string) => {
      navigator.clipboard.writeText(email);
      const message: MessageData = {
        title: t('common.success'),
        description: t('organisation.receipts-capture-email-copied'),
        type: MessageStates.INFO,
      };
      dispatch(turnMessageOn(message));
    },
    [dispatch, t]
  );

  const effectiveGoogleFolderName: string | null = googleDisconnected
    ? null
    : googleDriveStatus?.folderName ?? null;

  const effectiveOneDriveFolderName: string | null = oneDriveDisconnected
    ? null
    : oneDriveStatus?.folderName ?? null;

  const hasConnectedFolder =
    Boolean(effectiveGoogleFolderName) || Boolean(effectiveOneDriveFolderName);

  const handleDisconnectGoogle = React.useCallback(() => {
    setGoogleDisconnected(true);
    dispatch(disconnectFolderRequest('google', connectionId));
  }, [dispatch, connectionId]);

  const handleDisconnectOneDrive = React.useCallback(() => {
    setOneDriveDisconnected(true);
    dispatch(disconnectFolderRequest('one', connectionId));
  }, [dispatch, connectionId]);

  const googleDriveScannerEnabled = true;

  const renderDisconnectControl = (onConfirm: () => void) => (
    <StyledPopconfirm
      title={t('organisation.receipts-disconnect-folder-title')}
      description={t('organisation.receipts-disconnect-folder-desc')}
      onConfirm={onConfirm}
      okText={t('common.disconnect')}
      cancelText={t('common.cancel')}
      placement="topRight"
      overlayStyle={{ width: '400px', maxWidth: '400px' }}
    >
      <span>
        <Tooltip
          title={t('organisation.receipts-disconnect-folder-title')}
          placement="top"
        >
          <IconActionButton
            type="button"
            aria-label={t('common.disconnect')}
          >
            <DisconnectGlyph />
          </IconActionButton>
        </Tooltip>
      </span>
    </StyledPopconfirm>
  );

  const disconnectOneDriveIconButton = renderDisconnectControl(
    handleDisconnectOneDrive
  );

  const disconnectGoogleIconButton = renderDisconnectControl(
    handleDisconnectGoogle
  );

  const renderFolderDisconnectMenuItem = (
    target: 'one' | 'google',
    providerLabel: string,
    showProviderInLabel: boolean
  ) => (
    <IconContextButton
      type="text"
      icon={<CloseOutlined style={{ width: '18px', height: '18px' }} />}
      onClick={() => {
        setFolderCaptureMenuOpen(false);
        setDisconnectFolderModalTarget(target);
      }}
    >
      {showProviderInLabel
        ? `${t('organisation.receipts-disconnect-folder-menu')} (${providerLabel})`
        : t('organisation.receipts-disconnect-folder-menu')}
    </IconContextButton>
  );

  if (layout === 'sidePanel') {
    const oneDriveDisabled = disabled;
    const googleDisabled = disabled || !googleDriveScannerEnabled;

    const oneDriveConnectedByEmail =
      oneDriveStatus?.connectedByUser?.email != null
        ? String(oneDriveStatus.connectedByUser.email)
        : null;
    const oneDriveConnectedOn = formatDriveScannerCreatedDate(
      oneDriveStatus?.createdDate
    );

    const googleConnectedByEmail =
      googleDriveStatus?.connectedByUser?.email != null
        ? String(googleDriveStatus.connectedByUser.email)
        : null;
    const googleConnectedOn = formatDriveScannerCreatedDate(
      googleDriveStatus?.createdDate
    );

    const oneDriveFolderIdResolved = pickDriveFolderId(oneDriveStatus);
    const googleFolderIdResolved = pickDriveFolderId(googleDriveStatus);
    const oneDriveDriveUrl = pickDriveUrl(oneDriveStatus);
    const googleDriveUrl = pickDriveUrl(googleDriveStatus);

    const oneDriveBrowseUrl = resolveDriveBrowseUrl({
      provider: 'oneDrive',
      driveUrl: oneDriveDriveUrl,
      drivePath: oneDriveStatus?.drivePath,
      folderId: oneDriveFolderIdResolved,
      driveId: oneDriveStatus?.driveId,
    });

    const googleBrowseUrl = resolveDriveBrowseUrl({
      provider: 'google',
      driveUrl: googleDriveUrl,
      drivePath: googleDriveStatus?.drivePath,
      folderId: googleFolderIdResolved,
      driveId: googleDriveStatus?.driveId,
    });

    const oneDrivePathDisplay = formatDrivePathDisplay(
      oneDriveStatus?.drivePath,
      effectiveOneDriveFolderName,
      'oneDrive'
    );

    const googlePathDisplay = formatDrivePathDisplay(
      googleDriveStatus?.drivePath,
      effectiveGoogleFolderName,
      'google'
    );

    const emailCapturePanel = (
      <SidePanelOuter $interactionDisabled={disabled}>
        <SidePanelHead>
          <SidePanelTitleRowWithActions style={{ marginBottom: 0 }}>
            <CardTitleSpan style={{ lineHeight: '24px', minWidth: 0 }}>
              {t('organisation.receipts-capture-email')}
            </CardTitleSpan>
            {scanEmailDetails?.email ? (
              <Popover
                placement="bottomLeft"
                arrow={false}
                trigger="click"
                open={emailCaptureMenuOpen}
                onOpenChange={setEmailCaptureMenuOpen}
                overlayStyle={{ zIndex: 1050 }}
                content={
                  <div className="context-menu-items">
                    <Space direction="vertical" style={{ rowGap: '0' }}>
                      <IconContextButton
                        type="text"
                        icon={
                          <RefreshIcon
                            style={{ width: '18px', height: '22px' }}
                          />
                        }
                        onClick={() => {
                          setEmailCaptureMenuOpen(false);
                          setRegenerateEmailModalOpen(true);
                        }}
                      >
                        {t('organisation.receipts-regenerate-email')}
                      </IconContextButton>
                    </Space>
                  </div>
                }
              >
                <ContextButton
                  type="text"
                  aria-label={t('organisation.receipts-capture-email-menu')}
                >
                  <EllipseIcon style={{ width: 18, height: 18 }} />
                </ContextButton>
              </Popover>
            ) : null}
          </SidePanelTitleRowWithActions>
          <Divider style={{ margin: '16px 0' }} />
          <SidePanelIntroText
            style={{
              marginBottom: scanEmailDetails?.email ? 10 : 12,
              paddingRight: 0,
            }}
          >
            {t('organisation.receipts-capture-email-tooltip')}
          </SidePanelIntroText>
        </SidePanelHead>
        {!scanEmailDetails?.email ? (
          <ExportButton
            style={{ marginBottom: 0, alignSelf: 'flex-start' }}
            onClick={() => generateEmail()}
          >
            {t('organisation.receipts-generate-email')}
          </ExportButton>
        ) : (
          <ConnectedMeta>
            <MetaRow>
              <CardInfo
                label={t('organisation.receipts-capture-email-label')}
                data={scanEmailDetails?.email}
                dataBreakWord
                labelDataGap={CONNECTED_FOLDER_LABEL_DATA_GAP}
                onCopy={() =>
                  handleCopyCaptureEmail(String(scanEmailDetails?.email ?? ''))
                }
                copyTitle={t('organisation.receipts-capture-email-copy')}
              />
            </MetaRow>
            {scanEmailDetails?.connectedBy ? (
              <MetaRow>
                <CardInfo
                  label={t('organisation.drive-folder-connected-by')}
                  data={scanEmailDetails?.connectedBy}
                  dataBreakWord
                  labelDataGap={CONNECTED_FOLDER_LABEL_DATA_GAP}
                />
              </MetaRow>
            ) : null}
            {scanEmailDetails?.connectedOn ? (
              <MetaRow>
                <CardInfo
                  label={t('organisation.drive-folder-connected-on')}
                  data={formatDriveScannerCreatedDate(
                    scanEmailDetails?.connectedOn
                  )}
                  labelDataGap={CONNECTED_FOLDER_LABEL_DATA_GAP}
                />
              </MetaRow>
            ) : null}
          </ConnectedMeta>
        )}
      </SidePanelOuter>
    );

    return (
      <>
        <SidePanelStack>
        <SidePanelOuter $interactionDisabled={disabled}>
          <SidePanelHead>
            {hasConnectedFolder ? (
              <SidePanelTitleRowWithActions style={{ marginBottom: 0 }}>
                <CardTitleSpan style={{ lineHeight: '24px', minWidth: 0 }}>
                  {t('organisation.receipts-connect-folder')}
                </CardTitleSpan>
                <Popover
                  placement="bottomLeft"
                  arrow={false}
                  trigger="click"
                  open={folderCaptureMenuOpen}
                  onOpenChange={setFolderCaptureMenuOpen}
                  overlayStyle={{ zIndex: 1050 }}
                  destroyOnHidden={true}
                  content={
                    <div className="context-menu-items">
                      <Space direction="vertical" style={{ rowGap: '0' }}>
                        {effectiveOneDriveFolderName
                          ? renderFolderDisconnectMenuItem(
                              'one',
                              t('organisation.receipts-connect-folder-onedrive'),
                              Boolean(effectiveGoogleFolderName)
                            )
                          : null}
                        {effectiveGoogleFolderName
                          ? renderFolderDisconnectMenuItem(
                              'google',
                              t(
                                'organisation.receipts-connect-folder-google-drive'
                              ),
                              Boolean(effectiveOneDriveFolderName)
                            )
                          : null}
                      </Space>
                    </div>
                  }
                >
                  <ContextButton
                    type="text"
                    aria-label={t('organisation.receipts-connect-folder-menu')}
                  >
                    <EllipseIcon style={{ width: 18, height: 18 }} />
                  </ContextButton>
                </Popover>
              </SidePanelTitleRowWithActions>
            ) : (
              <SidePanelTitleRow>
                <CardTitleSpan style={{ lineHeight: '24px' }}>
                  {t('organisation.receipts-connect-folder')}
                </CardTitleSpan>
              </SidePanelTitleRow>
            )}
            <Divider style={{ margin: '16px 0' }} />
            <SidePanelIntroText>
              {hasConnectedFolder
                ? t('organisation.receipts-connect-folder-connected-tooltip')
                : t('organisation.receipts-connect-folder-tooltip')}
            </SidePanelIntroText>
          </SidePanelHead>

          {!hasConnectedFolder && (
            <SidePanelBodyColumn>
              {disabled && showDisabledMessage && (
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    lineHeight: 1.4,
                    color: 'var(--color-text-dark, #042a2a)',
                    marginTop: -4,
                  }}
                >
                  {t('organisation.capture-folder-connect-disabled')}
                </div>
              )}
              <SyncBlock>
                <SwitchLabeled
                  label={t('driveScanner.getAllExistignFilesLabel')}
                  tooltip={t('driveScanner.getAllExistingFilesTooltip')}
                  onChange={disabled ? () => {} : setSyncExistingFiles}
                  defaultChecked={syncExistingFiles}
                />
              </SyncBlock>
              <VerticalPickersColumn>
                <ProviderTilesRow>
                  <ProviderTile>
                    <OneDrivePicker
                      connectionId={connectionId}
                      folderName={effectiveOneDriveFolderName}
                      drivePath={oneDriveStatus?.drivePath}
                      syncExistingFiles={syncExistingFiles}
                      renderTrigger={({ onClick }) => {
                        const label = t(
                          'organisation.receipts-connect-folder-onedrive'
                        );
                        return (
                          <Tooltip title={label}>
                            <span>
                              <PickerIconButton
                                type="button"
                                aria-label={label}
                                onClick={oneDriveDisabled ? () => {} : onClick}
                                $disabled={oneDriveDisabled}
                              >
                                <PickerIconImg src={OneDriveIcon} alt={label} />
                              </PickerIconButton>
                            </span>
                          </Tooltip>
                        );
                      }}
                    />
                    <ProviderTileLabel>
                      {t('organisation.receipts-connect-folder-onedrive')}
                    </ProviderTileLabel>
                  </ProviderTile>

                  <ProviderTile>
                    <GoogleDrivePicker
                      connectionId={connectionId}
                      folderName={effectiveGoogleFolderName}
                      drivePath={googleDriveStatus?.drivePath}
                      syncExistingFiles={syncExistingFiles}
                      disabled={googleDisabled}
                      renderTrigger={({ onClick }) => {
                        const baseLabel = t(
                          'organisation.receipts-connect-folder-google-drive'
                        );
                        const tooltipLabel = googleDisabled
                          ? t(
                              'organisation.receipts-connect-folder-google-drive-coming-soon'
                            )
                          : baseLabel;
                        return (
                          <Tooltip title={tooltipLabel}>
                            <span>
                              <PickerIconButton
                                type="button"
                                aria-label={tooltipLabel}
                                onClick={googleDisabled ? () => {} : onClick}
                                $disabled={googleDisabled}
                              >
                                <PickerIconImg
                                  src={GoogleDriveIcon}
                                  alt={baseLabel}
                                />
                              </PickerIconButton>
                            </span>
                          </Tooltip>
                        );
                      }}
                    />
                    <ProviderTileLabel>
                      {t('organisation.receipts-connect-folder-google-drive')}
                    </ProviderTileLabel>
                  </ProviderTile>
                </ProviderTilesRow>
              </VerticalPickersColumn>
            </SidePanelBodyColumn>
          )}

          {hasConnectedFolder && (
            <ConnectedStack>
              {effectiveOneDriveFolderName && (
                <ConnectedBlock>
                  <ConnectedTopRow>
                    <ConnectedProviderStart>
                      <ProviderIcon src={OneDriveIcon} alt="" />
                      <ProviderNameText $weight={400}>
                        {t('organisation.receipts-connect-folder-onedrive')}
                      </ProviderNameText>
                      <ColoredTag
                        color="var(--color-status-success, #058181)"
                        bgcolor="var(--color-status-success-bg, #E9F6F7)"
                        bordered
                        bordercolor="var(--color-border, #dbeae3)"
                        icon={<CheckOutlined />}
                        style={{ margin: 0 }}
                      >
                        {t('common.active')}
                      </ColoredTag>
                    </ConnectedProviderStart>
                  </ConnectedTopRow>
                  <ConnectedMeta>
                    {oneDrivePathDisplay || oneDriveBrowseUrl ? (
                      <MetaRow>
                        <CardInfo
                          label={t('organisation.folder-path-or-url')}
                          data={oneDrivePathDisplay ?? oneDriveBrowseUrl ?? ''}
                          dataBreakWord
                          labelDataGap={CONNECTED_FOLDER_LABEL_DATA_GAP}
                          externalLinkHref={oneDriveBrowseUrl ?? undefined}
                          externalLinkTitle={t(
                            'organisation.drive-folder-open-link'
                          )}
                        />
                      </MetaRow>
                    ) : null}
                    {oneDriveConnectedByEmail ? (
                      <MetaRow>
                        <CardInfo
                          label={t('organisation.drive-folder-connected-by')}
                          data={oneDriveConnectedByEmail}
                          dataBreakWord
                          labelDataGap={CONNECTED_FOLDER_LABEL_DATA_GAP}
                        />
                      </MetaRow>
                    ) : null}
                    {oneDriveConnectedOn ? (
                      <MetaRow>
                        <CardInfo
                          label={t('organisation.drive-folder-connected-on')}
                          data={oneDriveConnectedOn}
                          labelDataGap={CONNECTED_FOLDER_LABEL_DATA_GAP}
                        />
                      </MetaRow>
                    ) : null}
                  </ConnectedMeta>
                </ConnectedBlock>
              )}

              {effectiveGoogleFolderName && (
                <ConnectedBlock>
                  <ConnectedTopRow>
                    <ConnectedProviderStart>
                      <ProviderIcon src={GoogleDriveIcon} alt="" />
                      <ProviderNameText $weight={600}>
                        {t('organisation.receipts-connect-folder-google-drive')}
                      </ProviderNameText>
                      <ColoredTag
                        color="var(--color-status-success, #058181)"
                        bgcolor="var(--color-status-success-bg, #E9F6F7)"
                        bordered
                        bordercolor="var(--color-border, #dbeae3)"
                        icon={<CheckOutlined />}
                        style={{ margin: 0 }}
                      >
                        {t('common.active')}
                      </ColoredTag>
                    </ConnectedProviderStart>
                  </ConnectedTopRow>
                  <ConnectedMeta>
                    {googlePathDisplay || googleBrowseUrl ? (
                      <MetaRow>
                        <CardInfo
                          label={t('organisation.folder-path-or-url')}
                          data={googlePathDisplay ?? googleBrowseUrl ?? ''}
                          dataBreakWord
                          labelDataGap={CONNECTED_FOLDER_LABEL_DATA_GAP}
                          externalLinkHref={googleBrowseUrl ?? undefined}
                          externalLinkTitle={t(
                            'organisation.drive-folder-open-link'
                          )}
                        />
                      </MetaRow>
                    ) : null}
                    {googleConnectedByEmail ? (
                      <MetaRow>
                        <CardInfo
                          label={t('organisation.drive-folder-connected-by')}
                          data={googleConnectedByEmail}
                          dataBreakWord
                          labelDataGap={CONNECTED_FOLDER_LABEL_DATA_GAP}
                        />
                      </MetaRow>
                    ) : null}
                    {googleConnectedOn ? (
                      <MetaRow>
                        <CardInfo
                          label={t('organisation.drive-folder-connected-on')}
                          data={googleConnectedOn}
                          labelDataGap={CONNECTED_FOLDER_LABEL_DATA_GAP}
                        />
                      </MetaRow>
                    ) : null}
                  </ConnectedMeta>
                </ConnectedBlock>
              )}
            </ConnectedStack>
          )}
        </SidePanelOuter>

        {emailCapturePanel}
        </SidePanelStack>

        <ConfirmModal
          open={regenerateEmailModalOpen}
          title={t('organisation.receipts-regenerate-email-modal-title')}
          onCancel={() => setRegenerateEmailModalOpen(false)}
          size="compact"
          cancelAction={{
            label: t('common.cancel'),
            onClick: () => setRegenerateEmailModalOpen(false),
          }}
          primaryAction={{
            label: t('common.proceed'),
            onClick: () => {
              generateEmail();
              setRegenerateEmailModalOpen(false);
            },
          }}
        >
          <p style={{ margin: 0 }}>
            {t('organisation.receipts-regenerate-email-modal-description')}
          </p>
        </ConfirmModal>

        <ConfirmModal
          open={disconnectFolderModalTarget != null}
          title={t('organisation.receipts-disconnect-folder-title')}
          onCancel={() => setDisconnectFolderModalTarget(null)}
          size="compact"
          cancelAction={{
            label: t('common.cancel'),
            onClick: () => setDisconnectFolderModalTarget(null),
          }}
          primaryAction={{
            label: t('common.disconnect'),
            variant: 'danger',
            onClick: () => {
              if (disconnectFolderModalTarget === 'one') {
                handleDisconnectOneDrive();
              } else if (disconnectFolderModalTarget === 'google') {
                handleDisconnectGoogle();
              }
              setDisconnectFolderModalTarget(null);
            },
          }}
        >
          <p style={{ margin: 0 }}>
            {t('organisation.receipts-disconnect-folder-desc')}
          </p>
        </ConfirmModal>
      </>
    );
  }

  return (
    <Wrapper $sidePanel={false}>
      {hasConnectedFolder && (
        <HistoryCorner>
          <DriveUploadHistory connectionId={connectionId} disabled={disabled} />
        </HistoryCorner>
      )}
      <HeaderRow>
        <LabelRow>
          {t('organisation.receipts-connect-folder')}
          <QuestionMarkTooltip
            title={t('organisation.receipts-connect-folder-tooltip')}
          />
        </LabelRow>
      </HeaderRow>

      {!hasConnectedFolder && (
        <PickerRow>
          <SwitchLabeled
            label={t('driveScanner.getAllExistignFilesLabel')}
            tooltip={t('driveScanner.getAllExistingFilesTooltip')}
            onChange={setSyncExistingFiles}
            defaultChecked={syncExistingFiles}
          />
          <InlinePickerGroup>
            <OneDrivePicker
              connectionId={connectionId}
              folderName={effectiveOneDriveFolderName}
              drivePath={oneDriveStatus?.drivePath}
              syncExistingFiles={syncExistingFiles}
            />
            <GoogleDrivePicker
              connectionId={connectionId}
              folderName={effectiveGoogleFolderName}
              drivePath={googleDriveStatus?.drivePath}
              syncExistingFiles={syncExistingFiles}
              disabled={!googleDriveScannerEnabled}
            />
          </InlinePickerGroup>
        </PickerRow>
      )}

      {hasConnectedFolder && (
        <ConnectedList>
          {effectiveOneDriveFolderName && (
            <ConnectedItem>
              <ProviderIcon src={OneDriveIcon} alt="" />
              <Tooltip title={effectiveOneDriveFolderName} placement="top">
                <FolderName>{effectiveOneDriveFolderName}</FolderName>
              </Tooltip>
              {disconnectOneDriveIconButton}
            </ConnectedItem>
          )}

          {effectiveGoogleFolderName && (
            <ConnectedItem>
              <ProviderIcon src={GoogleDriveIcon} alt="" />
              <Tooltip title={effectiveGoogleFolderName} placement="top">
                <FolderName>{effectiveGoogleFolderName}</FolderName>
              </Tooltip>
              {disconnectGoogleIconButton}
            </ConnectedItem>
          )}
        </ConnectedList>
      )}
    </Wrapper>
  );
};

export default ConnectFolder;
